import { __assign } from '../../node_modules/tslib/tslib.es6.js';
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useMemo, useCallback } from 'react';

var TreeContext = createContext({
    expandedState: {},
    setExpandedState: function () { return ({}); },
    flatIds: [],
    ref: null
});
var useTreeContext = function () {
    return useContext(TreeContext);
};
var TreeContextProvider = function (_a) {
    var children = _a.children, value = _a.value;
    return (jsx(TreeContext.Provider, __assign({ value: value }, { children: children })));
};
var useExpandedState = function (node) {
    var isExpandable = !!node.children && !node.disabled;
    var _a = useTreeContext(), expandedState = _a.expandedState, expandedByDefault = _a.expandedByDefault, setExpandedState = _a.setExpandedState, flatIds = _a.flatIds;
    var isExpanded = useMemo(function () {
        if (expandedState[node.id] !== undefined) {
            return Boolean(expandedState[node.id]);
        }
        return expandedByDefault || false;
    }, [expandedState, expandedByDefault, node.id]);
    return {
        isExpanded: isExpanded && isExpandable,
        isExpandable: isExpandable,
        toggleExpand: function (expand) {
            return setExpandedState(function (state) {
                var _a;
                return (__assign(__assign({}, state), (_a = {}, _a[node.id] = expand, _a)));
            });
        },
        expandAll: function () { return setExpandedState(flatIds.reduce(function (acc, id) {
            var _a;
            return (__assign(__assign({}, acc), (_a = {}, _a[id] = true, _a)));
        }, {})); }
    };
};
var useNodeActions = function (node) {
    var _a = useTreeContext(), onToggleCheckbox = _a.onToggleCheckbox, flatIds = _a.flatIds, onClick = _a.onClick, ref = _a.ref;
    var _b = useExpandedState(node.data), isExpanded = _b.isExpanded, toggleExpand = _b.toggleExpand, expandAll = _b.expandAll;
    var onClickNode = useCallback(function (node) {
        if (!node.disabled) {
            if (!node.children) {
                if (node.onClick) {
                    node.onClick();
                }
                else {
                    onClick === null || onClick === void 0 ? void 0 : onClick(node);
                }
            }
            else {
                toggleExpand(!isExpanded);
            }
        }
    }, [onClick, isExpanded, toggleExpand]);
    var onKeyDown = useCallback(function (e) {
        var _a, _b, _c, _d, _e;
        var moveFocus = function (up) {
            var _a;
            var foundIndex = flatIds.findIndex(function (id) { return id === node.id; });
            var remainingIds = up
                ? flatIds.slice(0, foundIndex).reverse()
                : flatIds.slice(foundIndex + 1);
            for (var _i = 0, remainingIds_1 = remainingIds; _i < remainingIds_1.length; _i++) {
                var id = remainingIds_1[_i];
                var found = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-tree-item-id='".concat(id, "']"));
                if (found) {
                    found.focus();
                    break;
                }
            }
        };
        // No Enter here because if it's a href then we want it to work as is
        var keys = [' ', 'ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft', 'Home', 'End', '*'];
        if (keys.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (e.key === 'Enter') {
            e.stopPropagation();
            if (node.data.children || node.data.onClick || !node.data.href) {
                // Enter: Toggle the expanded or collapsed state of the selected parent node.
                e.preventDefault();
                onClickNode(node.data);
            }
        }
        if (e.key === ' ') {
            // Space: Select the node
            onToggleCheckbox === null || onToggleCheckbox === void 0 ? void 0 : onToggleCheckbox(node);
        }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            // Up: Select the previous visible tree item.
            // Down: Select next visible tree item.
            moveFocus(e.key === 'ArrowUp');
        }
        else if (e.key === 'ArrowRight') {
            // Right: Expand the currently selected parent node and move to the first child list item.
            if (node.data.children) {
                if (isExpanded) {
                    moveFocus(false);
                }
                else {
                    toggleExpand(true);
                }
            }
        }
        else if (e.key === 'ArrowLeft') {
            // Left: Collapse the currently selected parent node if it is expanded. Move to the previous parent node (if possible) when the current parent node is collapsed.
            if (node.data.children) {
                if (isExpanded) {
                    toggleExpand(false);
                }
                else {
                    if (node.parent) {
                        (_b = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-tree-item-id='".concat(node.parent.id, "']"))) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                }
            }
        }
        else if (e.key === 'Home') {
            // Home: Select the root parent node of the tree.
            (_d = (_c = ref === null || ref === void 0 ? void 0 : ref.current) === null || _c === void 0 ? void 0 : _c.querySelector("[data-tree-item-id='".concat(flatIds[0], "']"))) === null || _d === void 0 ? void 0 : _d.focus();
        }
        else if (e.key === 'End') {
            // End: Select the last visible node of the tree.
            var reversedIds = flatIds.slice().reverse();
            for (var _i = 0, reversedIds_1 = reversedIds; _i < reversedIds_1.length; _i++) {
                var id = reversedIds_1[_i];
                var found = (_e = ref === null || ref === void 0 ? void 0 : ref.current) === null || _e === void 0 ? void 0 : _e.querySelector("[data-tree-item-id='".concat(id, "']"));
                if (found) {
                    found.focus();
                    break;
                }
            }
        }
        else if (e.key === '*') {
            // '*' (asterisk on the numpad): Expand all group nodes.
            expandAll();
        }
    }, [onClickNode, onToggleCheckbox]);
    return {
        onClickNode: onClickNode,
        onKeyDown: onKeyDown
    };
};
var useCheckableNode = function (node, parentChecked) {
    var checkedIds = useTreeContext().checkedIds;
    var isChildChecked = useMemo(function () {
        var anyChildChecked = function (childNode) {
            if (!childNode.children)
                return false;
            return childNode.children.some(function (x) { return (checkedIds === null || checkedIds === void 0 ? void 0 : checkedIds.includes(x.id)) || anyChildChecked(x); });
        };
        return anyChildChecked(node);
    }, [checkedIds]);
    var isChecked = useMemo(function () {
        return (checkedIds === null || checkedIds === void 0 ? void 0 : checkedIds.includes(node.id)) || parentChecked;
    }, [checkedIds, parentChecked]);
    return {
        isChildChecked: isChildChecked,
        isChecked: isChecked
    };
};

export { TreeContext, TreeContextProvider, useCheckableNode, useExpandedState, useNodeActions, useTreeContext };
